import React, { useRef, useState, useEffect } from "react";
import { Camera, Trash2, ImagePlus, Crop, RefreshCw, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_DIM = 1400;

const compress = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("bad_image"));
    };
    img.src = url;
  });

/** Home hero card — the owner's own photo of their Tesla. */
export const VehiclePhotoCard = ({ vehicle }) => {
  const rawKey = vehicle?.vin || (vehicle?.id ? `VEH-${vehicle.id}` : null);
  // Masked catalog VINs (contain •) are placeholders — never fetch with them.
  const key = rawKey && !rawKey.includes("•") ? rawKey : null;
  const [ts, setTs] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | photo | empty
  const [busy, setBusy] = useState(false);
  const [pref, setPref] = useState("cropped"); // cropped | original
  const [recropping, setRecropping] = useState(false);
  const [badgeBusy, setBadgeBusy] = useState(false);
  const inputRef = useRef(null);

  const downloadListingBadge = async () => {
    if (badgeBusy) return;
    setBadgeBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/vehicle/photo/${encodeURIComponent(key)}/listing-badge.jpg`);
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const file = new File([blob], `eviq_listing_badge_${String(key).slice(-4)}.jpg`, { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "EVIQ listing badge" });
        } catch (err) {
          if (err?.name === "AbortError") return; // user closed the share sheet
          throw err;
        }
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success("Listing badge ready — add it to your car listing photos");
    } catch {
      toast.error("Couldn't build the listing badge — try again");
    } finally {
      setBadgeBusy(false);
    }
  };

  // When the VIN resolves (e.g. live Tesla data merges in after an initial
  // demo-vehicle render), retry the photo fetch — otherwise an early 404
  // against the placeholder VIN left the card stuck on "empty" forever
  // (the exact desktop-browser symptom: photo shows on phone, not desktop).
  useEffect(() => {
    setStatus("loading");
    setTs(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (status !== "photo" || !key) return;
    fetch(`${API_BASE}/api/vehicle/photo/${encodeURIComponent(key)}/meta`)
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => { if (m?.preferred) setPref(m.preferred); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, key]);

  const toggleFraming = async () => {
    const next = pref === "cropped" ? "original" : "cropped";
    try {
      const r = await fetch(`${API_BASE}/api/vehicle/photo/${encodeURIComponent(key)}/preference`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preferred: next }),
      });
      if (!r.ok) throw new Error();
      setPref(next);
      setTs(Date.now());
      toast.success(next === "original" ? "Showing your original framing" : "Showing the vehicle crop");
    } catch {
      toast.error("Couldn't switch framing — try again");
    }
  };

  const recrop = async () => {
    setRecropping(true);
    try {
      const r = await fetch(`${API_BASE}/api/vehicle/photo/${encodeURIComponent(key)}/recrop`, { method: "POST" });
      const j = await r.json();
      if (!r.ok) throw new Error();
      setPref("cropped");
      setTs(Date.now());
      j.cropped ? toast.success(j.detail) : toast.info(j.detail);
    } catch {
      toast.error("Re-crop failed — try again");
    } finally {
      setRecropping(false);
    }
  };

  if (!key) return null;
  const src = `${API_BASE}/api/vehicle/photo/${encodeURIComponent(key)}${ts ? `?t=${ts}` : ""}`;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compress(file);
      const r = await fetch(`${API_BASE}/api/vehicle/photo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, image: dataUrl }),
      });
      if (!r.ok) throw new Error("upload_failed");
      setTs(Date.now());
      setStatus("photo");
      toast.success("Photo saved — looking sharp");
    } catch {
      toast.error("Couldn't save the photo — try a different image");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    try {
      await fetch(`${API_BASE}/api/vehicle/photo/${encodeURIComponent(key)}`, { method: "DELETE" });
      setStatus("empty");
      toast.success("Photo removed");
    } catch {
      /* ignore */
    }
  };

  return (
    <div data-testid="vehicle-photo-card">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
        data-testid="vehicle-photo-input"
      />

      {status !== "empty" && (
        <div
          className={`relative rounded-2xl overflow-hidden border border-white/10 ${status === "loading" ? "h-0 border-0" : "h-44"}`}
        >
          <img
            src={src}
            alt={`${vehicle?.year || ""} ${vehicle?.short || vehicle?.model || "vehicle"}`}
            className="w-full h-full object-cover"
            onLoad={() => setStatus("photo")}
            onError={() => setStatus("empty")}
            data-testid="vehicle-photo-img"
          />
          {status === "photo" && (
            <>
              <div
                className="absolute inset-x-0 bottom-0 px-3 py-2 flex items-end justify-between"
                style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.75))" }}
              >
                <span className="text-white text-xs font-semibold drop-shadow">
                  {vehicle?.year} {vehicle?.short || vehicle?.model}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={toggleFraming}
                    aria-label={pref === "cropped" ? "Show original framing" : "Show vehicle crop"}
                    title={pref === "cropped" ? "Showing vehicle crop — tap for original" : "Showing original — tap for vehicle crop"}
                    data-testid="vehicle-photo-framing-toggle"
                    className={`rounded-full p-1.5 border transition-colors ${
                      pref === "cropped"
                        ? "bg-[#39FF14]/20 border-[#39FF14]/50 text-[#39FF14]"
                        : "bg-black/50 border-white/20 text-zinc-300 hover:text-white"
                    }`}
                  >
                    <Crop className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={downloadListingBadge}
                    disabled={badgeBusy}
                    aria-label="Download listing badge"
                    title="Download a 'certified report available' badge image for your car listing"
                    data-testid="vehicle-photo-listing-badge"
                    className="rounded-full p-1.5 bg-black/50 border border-white/20 text-[#00E5FF] hover:bg-black/70 transition-colors disabled:opacity-50"
                  >
                    <BadgeCheck className={`w-3.5 h-3.5 ${badgeBusy ? "animate-pulse" : ""}`} />
                  </button>
                  <button
                    type="button"
                    onClick={recrop}
                    disabled={recropping}
                    aria-label="Re-crop to vehicle"
                    title="Re-run vehicle detection and crop"
                    data-testid="vehicle-photo-recrop"
                    className="rounded-full p-1.5 bg-black/50 border border-white/20 text-white hover:bg-black/70 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${recropping ? "animate-spin" : ""}`} />
                  </button>
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    aria-label="Change photo"
                    data-testid="vehicle-photo-change"
                    className="rounded-full p-1.5 bg-black/50 border border-white/20 text-white hover:bg-black/70 transition-colors"
                  >
                    <Camera className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={onRemove}
                    aria-label="Remove photo"
                    data-testid="vehicle-photo-remove"
                    className="rounded-full p-1.5 bg-black/50 border border-white/20 text-zinc-300 hover:text-white transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {status === "empty" && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="vehicle-photo-upload-btn"
          className="w-full rounded-2xl border border-dashed px-4 py-4 flex items-center gap-3 text-left transition-colors hover:bg-white/[0.03]"
          style={{ borderColor: "rgba(0,229,255,0.3)" }}
        >
          <span
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full"
            style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.3)" }}
          >
            <ImagePlus className="w-4.5 h-4.5 w-5 h-5" style={{ color: "#00E5FF" }} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[13px] font-semibold text-white leading-tight">
              {busy ? "Saving your photo…" : `Add a photo of your ${vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla"}`}
            </span>
            <span className="block text-[11px] text-zinc-500 mt-0.5">
              Personalize your garage — snap one now or pick from your library.
            </span>
          </span>
          <Camera className="w-4 h-4 shrink-0" style={{ color: "#39FF14" }} />
        </button>
      )}
    </div>
  );
};
